import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Address } from 'viem';
import { calculateShareBalance, type ShareRatio } from './useEquityToken';
import type { SubgraphTokenBalance } from '@/app/api/v1/holderEquityTokens/route';

export type HolderEquityToken = {
  /** Token contract address */
  tokenAddress: Address;
  /** Token name (e.g., "Tokenized Coin") */
  name: string;
  /** Token symbol (e.g., "tcoin") */
  symbol: string;
  /** Token decimals */
  decimals: number;
  /** Raw token balance as bigint */
  balance: bigint;
  /** Share ratio for token-to-share conversion */
  shareRatio: ShareRatio;
  /** Raw share balance as bigint */
  shareBalance: bigint;
  /** Total supply as bigint */
  totalSupply: bigint;
  /** Whether the token is paused */
  paused: boolean;
};

type HolderEquityTokensApiResponse = {
  ok: boolean;
  data: SubgraphTokenBalance[];
  updatedAt: string;
};

export const QUERY_KEY_HOLDER_EQUITY_TOKENS = (params: { chainId: number; holder: string }) => [
  'HOLDER_EQUITY_TOKENS',
  params.chainId,
  params.holder,
];

async function fetchHolderEquityTokens(params: { chainId: number; holder: string }) {
  const { chainId, holder } = params;

  const searchParams = new URLSearchParams({
    chainId: String(chainId),
    holder,
  });

  const url = `/api/v1/holderEquityTokens?${searchParams.toString()}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch holder equity tokens: ${response.statusText}`);
  }

  const data = (await response.json()) as HolderEquityTokensApiResponse;

  if (!data.ok) {
    throw new Error('Holder equity tokens API returned an error');
  }

  return data.data;
}

function mapSubgraphToHolderEquityToken(item: SubgraphTokenBalance): HolderEquityToken {
  const decimals = item.token.decimals;
  const balance = BigInt(item.balance);
  const shareRatio: ShareRatio = {
    numerator: BigInt(item.token.shareRatioNumerator),
    denominator: BigInt(item.token.shareRatioDenominator),
  };
  const shareBalance = calculateShareBalance(balance, shareRatio) ?? 0n;

  return {
    tokenAddress: item.token.id as Address,
    name: item.token.name,
    symbol: item.token.symbol,
    decimals,
    balance,
    shareRatio,
    shareBalance,
    totalSupply: BigInt(item.token.totalSupply),
    paused: item.token.paused,
  };
}

/**
 * Fetches all equity tokens held by a given address from the Tangor subgraph.
 *
 * Returns token balances, metadata, and share ratios in a single API call.
 * Only returns tokens with a non-zero balance.
 *
 * @param address - The wallet address to fetch equity tokens for
 * @param chainId - The chain ID to query
 *
 * @example
 * ```tsx
 * function Portfolio() {
 *   const { address } = useAccount();
 *   const chainId = useChainId();
 *   const { equityTokens, isLoading } = useHolderEquityTokens(address, chainId);
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return equityTokens.map((token) => (
 *     <div key={token.tokenAddress}>
 *       <p>{token.symbol}: {token.balanceFormatted}</p>
 *       <p>Shares: {token.shareBalanceFormatted}</p>
 *     </div>
 *   ));
 * }
 * ```
 */
export function useHolderEquityTokens(address: Address | undefined, chainId: number) {
  const queryOptions = useMemo(
    () => ({
      queryKey: QUERY_KEY_HOLDER_EQUITY_TOKENS({ chainId, holder: address ?? '' }),
      queryFn: async () => fetchHolderEquityTokens({ chainId, holder: address as string }),
      enabled: !!address,
      staleTime: 30_000, // 30 seconds
    }),
    [chainId, address],
  );

  const { data, isLoading, error } = useQuery(queryOptions);

  const equityTokens = useMemo(() => {
    if (!data) return [];
    return data.map(mapSubgraphToHolderEquityToken);
  }, [data]);

  return {
    equityTokens,
    isLoading,
    error: error,
  };
}
